import { isMatching } from "ts-pattern";

import { remoteLog } from "./logging";

type FetchWithAuthOptions = {
  /**
   * If true, redirects the user to the login page if authentication fails.
   */
  redirectToLogin?: boolean;
};

/**
 * Wrapper around the native fetch function that handles authentication.
 * It checks for a specific error response from the server indicating that the user needs to log in.
 * If such a response is received, it redirects the user to the login page.
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  { redirectToLogin = true }: FetchWithAuthOptions = {},
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Frontend-Version": process.env.APP_VERSION,
      ...options.headers,
    },
    credentials: "include",
  });

  if (response.status == 403 && redirectToLogin) {
    const maybeData = await json(response);

    if (isMatching({ message: "E_LOGIN_REQUIRED", status: "error" }, maybeData)) {
      window.location.href = "/login";
      return response;
    }
  }

  const isExpectedErrorResponse = url.endsWith("/authenticate/whoami") && response.status == 403;

  if (!response.ok && !isExpectedErrorResponse) {
    const maybeData = await json(response);
    const error = {
      message: `Failed loading: ${response.status} ${response.statusText}`,
      data: maybeData,
    };
    remoteLog(error, url, { remoteLog: false });
  }

  return response;
}

/**
 * Helper function to parse JSON from a response.
 */
async function json(response: Response): Promise<unknown> {
  try {
    return await response.clone().json();
  } catch (e) {
    return {};
  }
}
